// Catalogue des démarches d'une collectivité : recherche, filtres par domaine, délai et nombre
// de pièces ; chaque ligne mène à la fiche. Lecture publique, sans compte.
import { Champ, EtatVide } from '@cc/ui';
import { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { useCatalogue } from '../api';
import { codeCollectiviteValide, useCollectiviteChoisie } from '../collectivite';
import { EnTeteCitoyen, Ligne, Liste } from '../commun';
import type { DemarcheCatalogue } from '../types';
import { coutCourt } from './PageAccueilCollectivite';
import styles from '../citoyen.module.css';

const normaliser = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const LIBELLES_DOMAINE: Record<string, string> = {
  dossier: 'Démarches générales',
  marche_municipal: 'Marchés',
  domaine_public: 'Domaine public',
  evenement: 'Événements',
  urbanisme: 'Urbanisme',
  social: 'Action sociale',
  equipement: 'Équipements',
};

type FiltreDelai = 'tous' | 'rapide' | 'moyen' | 'long';
type FiltrePieces = 'toutes' | 'aucune' | 'peu' | 'plusieurs';

function joursDelai(d: DemarcheCatalogue): number | null {
  return d.delais.reglementaire?.jours ?? d.delais.interne?.jours ?? null;
}

export function filtrerCatalogue(elements: DemarcheCatalogue[], f: { texte: string; domaine: string | null; delai: FiltreDelai; pieces: FiltrePieces }): DemarcheCatalogue[] {
  const texte = normaliser(f.texte.trim());
  return elements.filter((d) => {
    if (texte && !normaliser(`${d.libelle} ${d.description} ${d.domaine}`).includes(texte)) return false;
    if (f.domaine && d.domaine !== f.domaine) return false;
    const j = joursDelai(d);
    if (f.delai === 'rapide' && !(j !== null && j <= 15)) return false;
    if (f.delai === 'moyen' && !(j !== null && j > 15 && j <= 30)) return false;
    if (f.delai === 'long' && !(j === null || j > 30)) return false;
    const n = d.pieces.length;
    if (f.pieces === 'aucune' && n !== 0) return false;
    if (f.pieces === 'peu' && !(n >= 1 && n <= 2)) return false;
    if (f.pieces === 'plusieurs' && n < 3) return false;
    return true;
  });
}

function Chips<T extends string>({ libelle, valeur, options, onChange }: { libelle: string; valeur: T; options: { v: T; l: string }[]; onChange: (v: T) => void }) {
  return (
    <div role="group" aria-label={libelle} className={styles.filtres}>
      {options.map((o) => (
        <button key={o.v} type="button" className={styles.filtre} aria-pressed={valeur === o.v} onClick={() => onChange(o.v)}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

export function PageCatalogue() {
  const { code: brut } = useParams();
  const [choisie] = useCollectiviteChoisie();
  const code = codeCollectiviteValide(brut) ?? choisie?.code ?? null;
  const catalogue = useCatalogue(code);
  const [texte, setTexte] = useState('');
  const [domaine, setDomaine] = useState<string | null>(null);
  const [delai, setDelai] = useState<FiltreDelai>('tous');
  const [pieces, setPieces] = useState<FiltrePieces>('toutes');
  const elements = catalogue.data?.elements ?? [];
  const domaines = useMemo(() => [...new Set(elements.map((d) => d.domaine))], [elements]);
  const visibles = useMemo(() => filtrerCatalogue(elements, { texte, domaine, delai, pieces }), [elements, texte, domaine, delai, pieces]);
  if (!code) return <Navigate to="/citoyen" replace />;
  return (
    <>
      <EnTeteCitoyen titre="Démarches" surTitre={catalogue.data?.collectivite.nom ?? choisie?.nom} retour={`/citoyen/${code}`} libelleRetour="Accueil" />
      <div className={styles.recherche}>
        <Champ id="recherche-demarche" libelle="Rechercher une démarche" type="search" value={texte} onChange={(e) => setTexte(e.target.value)} autoComplete="off" placeholder="Terrasse, marché, attestation…" />
        {domaines.length > 1 ? (
          <Chips
            libelle="Domaine"
            valeur={domaine ?? ''}
            options={[{ v: '', l: 'Tous les domaines' }, ...domaines.map((d) => ({ v: d, l: LIBELLES_DOMAINE[d] ?? d }))]}
            onChange={(v) => setDomaine(v || null)}
          />
        ) : null}
        <Chips<FiltreDelai>
          libelle="Délai"
          valeur={delai}
          options={[
            { v: 'tous', l: 'Tout délai' },
            { v: 'rapide', l: '15 jours ou moins' },
            { v: 'moyen', l: '16 à 30 jours' },
            { v: 'long', l: 'Plus de 30 jours' },
          ]}
          onChange={setDelai}
        />
        <Chips<FiltrePieces>
          libelle="Pièces à fournir"
          valeur={pieces}
          options={[
            { v: 'toutes', l: 'Toutes' },
            { v: 'aucune', l: 'Sans pièce' },
            { v: 'peu', l: '1 ou 2 pièces' },
            { v: 'plusieurs', l: '3 pièces et plus' },
          ]}
          onChange={setPieces}
        />
      </div>
      <EtatRequete chargement={catalogue.isPending} erreur={catalogue.error} onReessayer={() => void catalogue.refetch()} fonction="Le catalogue des démarches" lignes={6}>
        <p className={styles.aide} role="status">
          {visibles.length} démarche{visibles.length > 1 ? 's' : ''} sur {elements.length}
        </p>
        {visibles.length === 0 ? (
          <EtatVide titre={elements.length === 0 ? 'Aucune démarche en ligne' : 'Aucune démarche ne correspond à ces critères'}>
            <p>{elements.length === 0 ? 'Cette collectivité n\'a pas encore publié de démarche sur le portail.' : 'Élargissez la recherche ou retirez un filtre.'}</p>
          </EtatVide>
        ) : (
          <Liste libelle="Démarches">
            {visibles.map((d) => (
              <Ligne
                key={d.code}
                href={`/citoyen/${code}/demarches/${d.code}`}
                titre={d.libelle}
                sousTitre={`${d.pieces.length === 0 ? 'Aucune pièce' : `${d.pieces.length} pièce${d.pieces.length > 1 ? 's' : ''}`}${d.delais.reglementaire ? ` · ${d.delais.reglementaire.libelle}` : ''} · ${LIBELLES_DOMAINE[d.domaine] ?? d.domaine}`}
                droite={coutCourt(d)}
              />
            ))}
          </Liste>
        )}
      </EtatRequete>
    </>
  );
}
